<template>
    <b-modal ref="modal" :title="isEditing ? 'Edit Auto Process Product' : 'Add Auto Process Product'"
        size="lg" @ok.prevent="save" :ok-disabled="saving || !isValid" ok-title="Save"
        cancel-title="Cancel" no-close-on-backdrop>
        <template v-if="saving">
            <b-spinner small class="mr-2" /> Saving...
        </template>
        <template v-else>
            <!-- Drug Selection (only for new entries) -->
            <div v-if="!isEditing" class="mb-3">
                <SearchComponent v-model="selectedDrug" label="Drug" search-u-r-l="/Drug"
                    :params="{ onlyActive: false, allSources: true }" clearable required
                    @input="onDrugSelected" />
            </div>
            <div v-else class="mb-3">
                <label><strong>Drug</strong></label>
                <div>{{ product.drugName }} <code v-if="product.ndc11">({{ formatNdc(product.ndc11) }})</code></div>
            </div>

            <hr />

            <!-- Toggles -->
            <div class="mb-3">
                <strong>Options</strong>
                <div class="toggle-grid mt-2">
                    <checkbox v-model="product.passthroughSig">
                        <help-popup placement="top">Use directions from the e-script as-is instead of default directions</help-popup>
                        Passthrough Sig
                    </checkbox>
                    <checkbox v-model="product.autoship">
                        <help-popup placement="top">
                            Automatically creates an Order containing the prescription. ⚠ Any patient copay
                            (Prescription.PatientPay) is NOT collected automatically — there is no downstream
                            job that charges the patient's card for an autoship-created Order. Only enable
                            this for prescriptions where the copay is $0 or you have another process for
                            collecting payment.
                        </help-popup>
                        Autoship
                    </checkbox>
                    <checkbox v-model="product.autoBillInsurance">
                        <help-popup placement="top">Automatically submit insurance claims during processing</help-popup>
                        Auto Bill Insurance
                    </checkbox>
                    <checkbox v-model="product.isConsignment">
                        <help-popup placement="top">Drug is consignment inventory and billed differently</help-popup>
                        Is Consignment
                    </checkbox>
                    <checkbox v-model="product.selectBasedOnInventory">
                        <help-popup placement="top">Automatically select therapeutically equivalent drugs based on inventory availability</help-popup>
                        Select Based On Inventory
                    </checkbox>
                    <checkbox v-model="product.createPartialPaymentOrder" :disabled="!product.autoship">
                        <help-popup placement="top">
                            When Autoship is on and the prescription has a non-zero copay, the auto-created
                            Order starts in Partial Payment status so the patient app surfaces it for online
                            payment and the AutoOutbound text reminder fires. Technicians can also collect the
                            copay from the admin Order page. No effect when Autoship is off or copay is $0.
                        </help-popup>
                        Partial Payment Order
                    </checkbox>
                </div>
                <div class="onhold-pair mt-2">
                    <checkbox v-model="product.onHold">
                        <help-popup placement="top">Place orders on hold after auto-processing</help-popup>
                        On Hold
                    </checkbox>
                    <OnHoldSubStatusSelect v-model="product.onHoldSubStatusId" :disabled="!product.onHold"
                                           class="onhold-substatus-select" />
                </div>
            </div>

            <hr />

            <!-- Value Fields -->
            <div class="mb-3">
                <strong>Settings</strong>
                <b-form-row class="mt-2">
                    <b-col md="4">
                        <b-form-group>
                            <template #label>
                                Days Supply Factor
                                <help-popup placement="top">Multiplier applied to the prescribed days supply (e.g. 1.0 = exact, 2.0 = double)</help-popup>
                            </template>
                            <b-input v-model.number="product.daysSupplyFactor" type="number" step="0.1" min="0"
                                size="sm" />
                        </b-form-group>
                    </b-col>
                    <b-col md="4">
                        <b-form-group>
                            <template #label>
                                Default Delivery
                                <help-popup placement="top">Delivery method assigned to orders for this drug when auto-processed</help-popup>
                            </template>
                            <b-form-select v-model="product.defaultDelivery" size="sm" :options="deliveryCodeOptions" />
                        </b-form-group>
                    </b-col>
                    <b-col md="4">
                        <b-form-group>
                            <template #label>
                                Default Cash Out
                                <help-popup placement="top">Price schedule used to compute the cash price when insurance is not billed. Leave blank to inherit the program's default.</help-popup>
                            </template>
                            <PriceScheduleSelect v-model="product.defaultCashOut" />
                        </b-form-group>
                    </b-col>
                </b-form-row>
                <b-form-row>
                    <b-col md="12">
                        <b-form-group>
                            <template #label>
                                Default Directions
                                <help-popup placement="top">Sig directions to use on the prescription. Leave blank to use directions from the e-script</help-popup>
                            </template>
                            <b-input v-model="product.defaultDirections" size="sm" placeholder="Leave blank to use e-script directions" />
                        </b-form-group>
                    </b-col>
                </b-form-row>
                <b-form-row>
                    <b-col md="12">
                        <b-form-group>
                            <template #label>
                                Webhook URL
                                <help-popup placement="top">URL to receive a POST notification when this drug is auto-processed</help-popup>
                            </template>
                            <b-input v-model="product.webhookURL" size="sm" placeholder="https://" />
                        </b-form-group>
                    </b-col>
                </b-form-row>
            </div>

            <hr />

            <!-- NDC Configuration -->
            <div class="mb-3">
                <strong>NDC Configuration</strong>
                <b-form-row class="mt-2">
                    <b-col md="6">
                        <b-form-group>
                            <template #label>
                                Accepted NDCs
                                <help-popup placement="top">NDCs that will trigger this product's auto-process rules. Press Enter to add.</help-popup>
                            </template>
                            <b-form-tags v-model="product.acceptedNDCs" tag-variant="info" placeholder="Add NDC..."
                                separator=" ,;" size="sm" />
                        </b-form-group>
                    </b-col>
                    <b-col md="6">
                        <b-form-group>
                            <template #label>
                                Preferred NDC
                                <help-popup placement="top">The NDC to dispense when multiple accepted NDCs match. Leave blank to auto-select</help-popup>
                            </template>
                            <b-input v-model="product.preferredNDC" size="sm" placeholder="Auto-selected" />
                        </b-form-group>
                    </b-col>
                </b-form-row>
            </div>
        </template>
    </b-modal>
</template>

<script lang="ts">
import axios from 'axios';
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { AutoProcessProduct, createDefaultAutoProcessProduct } from '@/models/AutoProcessProduct';
import { NotificationOptions } from '@/util/NotificationOptionsPresets';
import { Drug } from '@/models/Drug/Drug';
import SearchComponent from '@/components/SearchComponent.vue';
import Checkbox from '@/components/Inputs/Checkbox.vue';
import HelpPopup from '@/components/HelpPopup.vue';
import OnHoldSubStatusSelect from '@/components/Program/OnHoldSubStatusSelect.vue';
import PriceScheduleSelect from '@/components/Program/PriceScheduleSelect.vue';
import { DeliveryCode } from '@/models/DeliveryCode';
import { enumToOptions } from '@/util/EnumOptions';
import TextUtils from '@/util/TextUtils';

@Component({
    name: 'AutoProcessProductEditModal',
    components: { SearchComponent, Checkbox, HelpPopup, OnHoldSubStatusSelect, PriceScheduleSelect },
})
export default class AutoProcessProductEditModal extends Vue {
    @Prop({ required: true }) programId!: number;

    private product: AutoProcessProduct = createDefaultAutoProcessProduct(0);
    // Initialize as a Drug instance (not null) so SearchComponent sees a HasLabel
    // on its v-model and converts search results to Drug instances. With a null
    // v-model, modelObject stays as {} and printLabel falls through to `o.id`,
    // leaving the dropdown options blank.
    private selectedDrug: Drug | null = new Drug();
    private isEditing = false;
    private saving = false;

    // Built once at class-init time from the DeliveryCode enum mirror. Numeric
    // values pair with the worker's (int)Prescription.DeliveryCodeEnum cast in
    // CreateOrderForAutoShip.
    private deliveryCodeOptions = enumToOptions(DeliveryCode, { includeNone: true });

    get isValid(): boolean {
        return this.product.packageId > 0 && this.product.acceptedNDCs.length > 0;
    }

    // Mirror of AutoProcessBulkEditModal's watcher: createPartialPaymentOrder is
    // meaningless without autoship, so unchecking autoship must also clear the
    // partial-payment flag — otherwise we'd persist autoship=false +
    // createPartialPaymentOrder=true, which silently reactivates the moment autoship
    // is re-enabled.
    @Watch('product.autoship')
    onAutoshipChanged(val: boolean) {
        if (!val) this.product.createPartialPaymentOrder = false;
    }

    formatNdc(ndc: string | null): string {
        return TextUtils.formatNdc11(ndc);
    }

    open(existing: AutoProcessProduct | null) {
        if (existing) {
            this.isEditing = true;
            this.product = { ...existing, acceptedNDCs: [...existing.acceptedNDCs] };
        } else {
            this.isEditing = false;
            this.product = createDefaultAutoProcessProduct(this.programId);
        }
        this.selectedDrug = new Drug();
        (this.$refs.modal as any).show();
    }

    onDrugSelected(drug: Drug | null) {
        if (!drug) return;
        this.product.packageId = drug.packageID ?? drug.drugId;
        this.product.drugSource = drug.source;
        this.product.drugName = drug.productNameShort;
        this.product.ndc11 = drug.ndc;
        this.product.packageSize = drug.packageSize;

        if (drug.ndc && !this.product.acceptedNDCs.includes(drug.ndc)) {
            this.product.acceptedNDCs = [...this.product.acceptedNDCs, drug.ndc];
        }
        if (!this.product.preferredNDC && drug.ndc) {
            this.product.preferredNDC = drug.ndc;
        }
    }

    async save() {
        if (!this.isValid) return;
        this.saving = true;
        try {
            this.product.programId = this.programId;
            await axios.post(`/api/program/${this.programId}/auto-process-products`, this.product);
            (this as any).$notifications?.notify(
                NotificationOptions.success(this.isEditing ? 'Product updated.' : 'Product added.')
            );
            (this.$refs.modal as any).hide();
            this.$emit('saved');
        } catch (e: any) {
            (this as any).$notifications?.notify(NotificationOptions.error(e));
        } finally {
            this.saving = false;
        }
    }
}
</script>

<style scoped lang="scss">
.toggle-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 8px;
}

.toggle-grid label {
    white-space: nowrap;
}

.onhold-pair {
    display: flex;
    align-items: center;
    gap: 12px;
}

.onhold-pair ::v-deep .form-check {
    margin: 0;
    min-height: 0;
    display: flex;
    align-items: center;
}

.onhold-pair > .onhold-substatus-select {
    flex: 0 0 280px;
    max-width: 280px;
}

// Backstop for column alignment: if a long label + help-popup ever wraps to
// two lines again, align the b-cols at the bottom so the input controls stay
// on the same baseline across the row.
::v-deep .form-row {
    align-items: flex-end;
}

::v-deep .form-row .form-group {
    margin-bottom: 0;
    width: 100%;
}
</style>
